import { useCallback, useRef } from 'react';

import { PER_PAGE_TRANSACTIONS } from '@/features/cards/constants';
import {
  ICardTransaction,
  IDigitizedCard,
  ITransactionByDigitizedCard,
  TCardTransactionFormFilters,
} from '@/features/cards/store/models/card.model';
import { CardTransactionHeaders } from '@/features/cards/components/CardTransactionsTable/CardTransactionHeaders';
import { CardTransactionRows } from '@/features/cards/components/CardTransactionsTable/CardTransactionRows';

import { useCursor } from '@/hooks/useCursor';
import { useCardTransactionFilters } from '@/features/cards/hooks/CardTransactions/useCardTransactionFilters';
import { ICursor } from '@/store/models/generics.model';
import { useColumns } from '@/features/cards/context/CardTransactionColumnContext';

interface IUseCardTransactionsDataProps {
  cardTransactions?: ICardTransaction[];
  digitizedCards?: IDigitizedCard[];
  cursor?: ICursor;
  onFiltersChange?: ({ filters, cursorId }: { filters?: TCardTransactionFormFilters; cursorId?: string }) => void;
  isLoading?: boolean;
  onRowClick?: (transaction: ICardTransaction) => void;
}

export function useCardTransactionsData({
  cardTransactions,
  digitizedCards,
  onFiltersChange,
  cursor,
  isLoading,
  onRowClick,
}: IUseCardTransactionsDataProps) {
  const transactions: ITransactionByDigitizedCard[] =
    cardTransactions?.map((cardTransaction) => {
      const linkedDigitizedCard = cardTransaction.cardDigitalizationExternalId
        ? digitizedCards?.find(
            ({ cardDigitalizationExternalId }) =>
              cardDigitalizationExternalId === cardTransaction.cardDigitalizationExternalId,
          )
        : undefined;

      return linkedDigitizedCard
        ? {
            ...cardTransaction,
            tokenRequestor: linkedDigitizedCard.tokenRequestor,
            deviceType: linkedDigitizedCard.deviceType,
            deviceName: linkedDigitizedCard.deviceName,
            isLinkedToDizitedcard: true,
          }
        : cardTransaction;
    }) || [];

  const queryParameters = useRef({
    filters: {},
    cursorId: undefined,
  });

  const { activeHeaders, columnsConfig, isAllTransactions } = useColumns();
  const headers = CardTransactionHeaders({
    headersList: activeHeaders,
  });

  const rows = CardTransactionRows({ transactions, headersList: activeHeaders, columnsConfig, isLoading, onRowClick });

  const handleFilterChange = useCallback(
    (filtersParameters: TCardTransactionFormFilters) => {
      queryParameters.current = {
        ...queryParameters.current,
        filters: filtersParameters,
      };
      const { filters, cursorId } = queryParameters.current;
      onFiltersChange({ filters, cursorId });
    },
    [onFiltersChange],
  );

  const handleNavigation = useCallback(
    (cursorId: string) => {
      queryParameters.current = {
        ...queryParameters.current,
        cursorId,
      };

      const { filters } = queryParameters.current;
      onFiltersChange?.({ filters, cursorId });
    },
    [onFiltersChange],
  );

  const pagination = useCursor(cursor, PER_PAGE_TRANSACTIONS, handleNavigation);

  const { clearFilters, hasFilters, search, createdDate, amount, status, mcc, mid, paymentTypes } =
    useCardTransactionFilters({
      onChange: handleFilterChange,
    });

  const fields = isAllTransactions
    ? { createdDate }
    : {
        search,
        createdDate,
        amount,
        status,
        mcc,
        mid,
        paymentTypes,
      };

  return {
    headers,
    rows,
    pagination,
    hasFilters,
    clearFilters,
    fields,
    setSearchValue: search.setSearchValue,
  };
}
